import React, { useEffect } from "react";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { useSpinner } from "../../customHooks";
import { DataAvailabilityMessages, Status } from "../../common/constants";
import {
  setDataFetched,
  userList,
  selector as userListApi,
} from "./listUserSlice";
import { selector as groupApi } from "../deviceManagement/groupSlice";
import useUserGroup from "./useUserGroup";
import { IFlatUser } from "./util";
import { IListUser } from "../../common/types/userTypes";
import { IGroup } from "../../common/types/groupTypes";
import { roleIds } from "../../config";
import {
  getRoleIdByName,
  getRoleName,
  isDirectAdmin,
  isSuccessResponse,
} from "../../common/utils";
import useNotification from "../../components/notification/useNotification";
import { inviteUser } from "../../components/inviteUserModal/inviteUserSlice";
import {
  getOrganisationLicenses,
  selector as licensingApi,
} from "../../components/modals/licensingModal/licensingSlice";
import { ILicensingDetail } from "../../common/types/mobileLicensingTypes";

const useUserTable = (orgId?: string) => {
  const [rows, setRows] = React.useState<any>([]);
  const dispatch: ThunkDispatch<any, undefined, Action> = useDispatch();
  const usersList: IListUser[] = useSelector(userListApi)?.users;
  const { formatUserData } = useUserGroup();
  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const groupIdToHierarchyMap: Record<string, string> =
    useSelector(groupApi).groupIdToHierarchyMap;
  const groups: IGroup[] = useSelector(groupApi).groups;
  const [globalFilter, setGlobalFilter] = React.useState<string>("");
  const userListApiStatus: string = useSelector(userListApi)?.status;
  const groupApiStatus: string = useSelector(groupApi).status;
  const organisationLicenses = useSelector(licensingApi);
  const organisationLicensesStatus = organisationLicenses.status;
  const organisationLicensingDetails: any =
    organisationLicenses.licensingDetails;

  const userRoleName: string = getRoleName();
  useSpinner(userListApiStatus);
  if (userListApiStatus == Status.IDLE && userRoleName !== roleIds.superAdmin) {
    dispatch(userList());
  } 

  useEffect(() => {
    if (organisationLicensesStatus == Status.IDLE ) {
        if(isDirectAdmin()){
          dispatch(getOrganisationLicenses(orgId));
        }
      }
  },[organisationLicensesStatus]);
  
  const filterUsersByOrgId = (
    data: IFlatUser[],
    orgId: string
  ): IFlatUser[] => {
    return data.filter((user) => user.organization === orgId);
  };

  useEffect(() => {
    if (userRoleName !== roleIds.superAdmin) {
    dispatch(userList());
  }
  }, [userRoleName]);

  const handleReinvite = async (userData) => {
    try {
      let updateUserData = {
        email: userData.email,
        name: userData.userName,
        organizationName: userData.groupId,
        organizationId: userData.organization,
        groupId: userData.groupName,
        role: getRoleIdByName(userData.roleId),
        isPasswordEnable: userData.isPasswordEnable,
        isReinvite:true,
      };

      await dispatch(inviteUser(updateUserData)).then((response: any) => {
        if (isSuccessResponse(response.payload)) {
          showSuccessNotification(response.payload.message);
        } else {
          showWarningNotification(response.payload.response.data.message);
        }
      });
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  useEffect(() => {
    if (Object.keys(groupIdToHierarchyMap).length > 0) {
      const formatedUserData: IFlatUser[] = formatUserData(usersList);
      if (orgId) {
        const filterterdUsers: IFlatUser[] = filterUsersByOrgId(
          formatedUserData,
          orgId
        );
        setRows(filterterdUsers);
      } else {
        setRows(formatedUserData);
      }
      dispatch(setDataFetched(true));
    }
  }, [usersList, groupIdToHierarchyMap, groups]);

  const noDataMessage =
    groupApiStatus === Status.PENDING || userListApiStatus === Status.PENDING
      ? DataAvailabilityMessages.USER_DATA_LOADING
      : DataAvailabilityMessages.NO_USER_AVAILABLE;

  return {
    rows,
    globalFilter,
    setGlobalFilter,
    noDataMessage,
    handleReinvite,
    organisationLicensingDetails,
  };
};

export default useUserTable;
